import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function initClubSwiper() {
  const section = document.querySelector('.section-4');
  const stickyArea = document.querySelector('.club-sticky-area');
  const container = document.querySelector('.club-swiper-container');
  if (!section || !stickyArea || !container) return;

  const wrapper = container.querySelector('.club-swiper-wrapper');

  // Clean up any old clone elements if they exist
  container.querySelectorAll('.is-clone').forEach(el => el.remove());

  const slides = Array.from(container.querySelectorAll('.club-slide'));
  const controlsRow = stickyArea.querySelector('.club-controls-row');
  const dotsContainer = controlsRow ? controlsRow.querySelector('.club-swiper-dots') : null;

  if (slides.length === 0) return;

  const total = slides.length;

  // Helper calculation functions
  function getSlideWidth() {
    return slides[0].offsetWidth;
  }

  function getGapSize() {
    const computedGap = window.getComputedStyle(wrapper).gap || window.getComputedStyle(wrapper).columnGap;
    const parsed = parseFloat(computedGap);
    return isNaN(parsed) ? 24 : parsed;
  }

  function getMaxTranslateX() {
    const slideWidth = getSlideWidth();
    const gap = getGapSize();
    return (total - 1) * (slideWidth + gap);
  }

  function getNavHeight() {
    const nav = document.querySelector('.navbar');
    return nav ? nav.offsetHeight : 70;
  }

  // Active state tracker
  let activeIndex = 0;
  function updateActiveSlide(idx) {
    if (idx === activeIndex) return;
    slides[activeIndex].classList.remove('active');
    if (dots[activeIndex]) dots[activeIndex].classList.remove('active');

    activeIndex = idx;
    slides[activeIndex].classList.add('active');
    if (dots[activeIndex]) dots[activeIndex].classList.add('active');
  }

  // 1. Create Dots Pagination
  if (dotsContainer) {
    dotsContainer.innerHTML = '';
    slides.forEach((slide, idx) => {
      const dot = document.createElement('div');
      dot.classList.add('club-dot');
      if (idx === 0) dot.classList.add('active');
      dot.addEventListener('click', () => {
        const st = ScrollTrigger.getById('club-scroll-trigger');
        if (st) {
          const targetProgress = idx / (total - 1);
          const targetScroll = st.start + targetProgress * (st.end - st.start);
          if (window.lenis) {
            window.lenis.scrollTo(targetScroll);
          } else {
            window.scrollTo({ top: targetScroll, behavior: 'smooth' });
          }
        }
      });
      dotsContainer.appendChild(dot);
    });
  }

  const dots = dotsContainer ? Array.from(dotsContainer.querySelectorAll('.club-dot')) : [];

  // Set initial wrapper position
  gsap.set(wrapper, { x: 0 });

  // 2. Create Sticky Pin ON ENTIRE SECTION 4 + Horizontal Scroll Timeline
  // Pins the whole section so title, description, and slider stay pinned together on screen
  const getHoldBuffer = () => Math.max(500, window.innerHeight * 0.7);

  const tl = gsap.timeline({
    scrollTrigger: {
      id: 'club-scroll-trigger',
      trigger: section,
      start: 'top top',
      end: () => `+=${getMaxTranslateX() + getHoldBuffer()}`, // Travel distance + hold buffer so slide 5 stays held before Section 5 unpins
      pin: section,
      pinSpacing: true,
      anticipatePin: 1,
      scrub: 0.6,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const totalTravel = getMaxTranslateX();
        const totalEnd = totalTravel + getHoldBuffer();
        const travelProgress = Math.min(1, (self.progress * totalEnd) / (totalTravel || 1));
        const rawIdx = Math.round(travelProgress * (total - 1));
        const currentIdx = Math.max(0, Math.min(rawIdx, total - 1));
        updateActiveSlide(currentIdx);
      }
    }
  });

  // Translate horizontal slider track to final slide
  tl.to(wrapper, {
    x: () => -getMaxTranslateX(),
    ease: 'none',
    duration: 1
  });

  // Hold final slide static on screen before Section 4 unpins and Section 5 scrolls up
  tl.to({}, { duration: 0.3 });

  // 3. Mouse Drag & Touch Swipe Support
  let isDragging = false;
  let startX = 0;
  let initialWrapperX = 0;

  function getWrapperX() {
    return gsap.getProperty(wrapper, 'x') || 0;
  }

  function onDragStart(e) {
    isDragging = true;
    container.style.cursor = 'grabbing';
    startX = e.pageX || (e.touches && e.touches[0].pageX) || 0;
    initialWrapperX = getWrapperX();
  }

  function onDragMove(e) {
    if (!isDragging) return;
    const currentPointerX = e.pageX || (e.touches && e.touches[0].pageX) || 0;
    const deltaX = currentPointerX - startX;
    let newX = initialWrapperX + deltaX;

    const maxX = 0;
    const minX = -getMaxTranslateX();

    if (newX > maxX) {
      newX = maxX + (newX - maxX) * 0.2;
    } else if (newX < minX) {
      newX = minX + (newX - minX) * 0.2;
    }

    gsap.set(wrapper, { x: newX });

    const slideWidth = getSlideWidth();
    const gap = getGapSize();
    const rawIdx = Math.round(-newX / (slideWidth + gap));
    const clampedIdx = Math.max(0, Math.min(rawIdx, total - 1));
    updateActiveSlide(clampedIdx);
  }

  function onDragEnd() {
    if (!isDragging) return;
    isDragging = false;
    container.style.cursor = 'grab';

    const st = ScrollTrigger.getById('club-scroll-trigger');
    if (st) {
      const slideWidth = getSlideWidth();
      const gap = getGapSize();
      const currentWrapperX = getWrapperX();
      const targetIdx = Math.max(0, Math.min(Math.round(-currentWrapperX / (slideWidth + gap)), total - 1));
      const targetProgress = targetIdx / (total - 1);
      const targetScroll = st.start + targetProgress * (st.end - st.start);
      if (window.lenis) {
        window.lenis.scrollTo(targetScroll);
      } else {
        window.scrollTo({ top: targetScroll, behavior: 'smooth' });
      }
    }
  }

  container.style.cursor = 'grab';
  container.addEventListener('mousedown', onDragStart);
  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('mouseup', onDragEnd);

  // Refresh ScrollTrigger after layout renders & images load
  const refreshST = () => ScrollTrigger.refresh();
  setTimeout(refreshST, 300);
  window.addEventListener('load', refreshST);

  console.log('Club section sticky slider area end distance synchronized for immediate release.');
}
